/**
 * CodeMirror 6 extension for highlighting Magic MD plugin syntax:
 *   ::define[id]{plugin="name"}  ...  ::
 *   ::render[id]::
 *   $ref{id.output.path}
 *
 * Also adds clickable line decorations on define/render headers,
 * plus live execution status badges (idle/running/success/error).
 *
 * Public API barrel — re-exports all sub-modules organized by feature
 * (define/, render/, ref/, shared/).
 */

import type { Extension } from "@codemirror/state";
import { commentTheme } from "./comment/theme";
import { contentTheme } from "./content/theme";
import { forTheme } from "./control-flow/for-theme";
import { buttonGroupEventFilter } from "./define/event-handlers";
import { defineTheme } from "./define/theme";
import { magicDragController, magicDragTheme } from "./drag-controller";
import { fenceTheme } from "./fence/theme";
// Drag handle is now rendered inside the bookmarks gutter (codemirror-bookmarks).
// We still load the controller (pointer events) and theme; the standalone
// `magicDragGutter()` is intentionally NOT registered here.
import { autoFoldPlugin, magicBlockFoldService, magicFoldGutterLineClass } from "./fold";
import { importTheme } from "./import/theme";
import { markdownLinkTheme } from "./markdown-link/theme";
import { markdownMathTheme } from "./markdown-math/theme";
import {
    markdownRichBlockField,
    markdownRichFocusSync,
} from "./markdown-rich/block-field";
import { markdownRichTheme } from "./markdown-rich/theme";
import { magicSyntaxKeymap } from "./keyboard";
import { metaTheme } from "./meta/theme";
import { magicSyntaxPlugin } from "./plugin";
import { refTheme } from "./ref/theme";
import { inlineRenderBlockField } from "./render/inline-render-block-field";
import { renderTheme } from "./render/theme";
import { magicScanField } from "./scan-state";
import { sharedTheme, spinKeyframes } from "./shared/theme";
import {
    cloudWorkerPluginNamesField,
    engineDefinitionsField,
    executionDurationField,
    executionErrorField,
    executionStatusField,
    forceL2Field,
    forExecutionDurationField,
    forExecutionStatusField,
    inlineRenderedDefinitionsField,
    magicSyntaxFocusedField,
    parseDiagnosticsField,
    refResolveContextField,
    watchedUnitIdsField,
} from "./state-fields";
import { useTheme } from "./use/theme";

/**
 * Export the complete Magic MD syntax extension (plugin + theme).
 */
export function magicMDSyntax(): Extension[] {
    return [
        executionStatusField,
        executionErrorField,
        executionDurationField,
        engineDefinitionsField,
        cloudWorkerPluginNamesField,
        forExecutionStatusField,
        forExecutionDurationField,
        inlineRenderedDefinitionsField,
        watchedUnitIdsField,
        parseDiagnosticsField,
        refResolveContextField,
        forceL2Field,
        magicSyntaxFocusedField,
        // magicScanField MUST come before consumers (magicSyntaxPlugin, autoFoldPlugin)
        // so that state.field(magicScanField) is always fresh when ViewPlugins update.
        magicScanField,
        markdownRichBlockField,
        markdownRichFocusSync,
        inlineRenderBlockField,
        magicSyntaxKeymap,
        buttonGroupEventFilter,
        magicSyntaxPlugin,
        sharedTheme,
        commentTheme,
        defineTheme,
        fenceTheme,
        renderTheme,
        refTheme,
        markdownLinkTheme,
        markdownMathTheme,
        markdownRichTheme,
        forTheme,
        metaTheme,
        importTheme,
        useTheme,
        contentTheme,
        spinKeyframes,
        magicBlockFoldService,
        magicFoldGutterLineClass,
        autoFoldPlugin,
        magicDragController(),
        magicDragTheme,
    ];
}

/* ── Re-exports for external consumers ── */

// Fold management (used by useCodeMirror)
export {
    buildFoldEffects,
    findFoldRangeStartingOnLine,
    foldAllBlocks,
    fullDocumentReplaceAnnotation,
    getFoldSnapshot,
    isMagicFoldCloseLineText,
    magicFoldGutterLineClass,
} from "./fold";
// Unified scan StateField (used by diagnostics and other consumers)
export { magicScanField } from "./scan-state";
export type { MagicDocumentAnalysis } from "./scan-state";
// State fields & effects (used by EditorPanel, useCodeMirror)
export {
    cloudWorkerPluginNamesField,
    engineDefinitionsField,
    executionDurationField,
    executionErrorField,
    executionStatusField,
    forceL2Field,
    forExecutionDurationField,
    forExecutionStatusField,
    inlineRenderedDefinitionsField,
    magicSyntaxFocusedField,
    parseDiagnosticsField,
    watchedUnitIdsField,
    setMagicSyntaxFocused,
    setEngineDefinitions,
    setExecutionDurations,
    setExecutionErrors,
    setExecutionStatuses,
    setCloudWorkerPluginNames,
    setForceL2Mode,
    setForExecutionDurations,
    setForExecutionStatuses,
    setInlineRenderedDefinitions,
    setParseDiagnostics,
    setRefResolveContext,
    setWatchedUnitIds,
} from "./state-fields";
export { exitActiveMagicSyntaxEdit, magicSyntaxKeymap } from "./keyboard";

// Types (used by external consumers)
export type { FoldSnapshot } from "./types";
